<template>
  <div>
    <select id="points_expiration_type"
            :disabled="!isEditable"
            name="merchant[points_expiration_type]"
            v-model="expirationOptions.expirationType"
            class="my-10"
    >
      <option>off</option>
      <option>time based</option>
      <option>visit based</option>
    </select>

    <div v-if="expirationOptions.expirationType === 'visit based'">
      <div class="sb-row bottom-spacing">
        <input type="number"
               step="1" min="30"
               id="point_expiration_without_visit"
               name="merchant[points_expiration_interval]"
               v-model="expirationOptions.daysSinceLastVisit"
               class="small margin-right pr-0"
               :class="{'has-error': hasDslvError}"
               :disabled="!isEditable"
        />

        <label>
          number of days without visit until points expiration (30 days minimum)
          <span class="form-field-notice error" v-if="dslvMinError">a minimum of 30 days is allowed</span>
          <span class="form-field-notice error" v-else-if="dslvDecError">decimal values are not allowed</span>
          <span class="form-field-notice error" v-if="dslvMaxError">a maximum of 2147483647 days is allowed</span>
        </label>

        <input type="radio"
               :disabled="!isEditable"
               class="points-radio margin-right"
               v-model="expirationOptions.visitBasedType"
               value="days_since_last_visit"
               id="days_since_last_visit">
      </div>

      <div class="sb-row bottom-spacing">
        <input type="number"
               step="1" min="30" max="2147483647"
               id="point_expiration_since_visit"
               name="merchant[points_expiration_interval]"
               v-model="expirationOptions.daysSincePointsIssued"
               class="small margin-right pr-0"
               :class="{'has-error': hasDspiError}"
               :disabled="!isEditable"
        />

        <label>
          number of days since visit until points removed from balance (30 days minimum)
          <span class="form-field-notice error" v-if="dspiMinError">a minimum of 30 days is allowed</span>
          <span class="form-field-notice error" v-else-if="dspiDecError">decimal values are not allowed</span>
          <span class="form-field-notice error" v-if="dspiMaxError">a maximum of 2147483647 days is allowed</span>
        </label>

        <input type="radio"
               class="points-radio margin-right"
               :disabled="!isEditable"
               v-model="expirationOptions.visitBasedType"
               value="days_since_points_issued"
               id="days_since_points_issued"/>
      </div>
    </div>

    <div v-if="expirationOptions.expirationType === 'time based'" class="bottom-spacing">
      <div class="sb-row start">
        points expire on the 1st of every
        <input type="number"
               step="1" min="1" :max="maximumMonthlyInterval"
               id="merchant_months_for_points_to_expire"
               name="merchant[months_for_points_to_expire]"
               v-model="expirationOptions.monthlyInterval"
               class="small time-expiration-input pr-0"
               :class="{'has-error': hasMonthlyError}"
               :disabled="!isEditable"
        >
        {{ expirationOptions.monthlyInterval | pluralize('month') }}
      </div>
      <span class="form-field-notice error" v-if="monthlyMinError">a minimum of 1 month is allowed</span>
      <span class="form-field-notice error" v-else-if="monthlyDecError">decimal values are not allowed</span>
      <span class="form-field-notice error" v-if="monthlyMaxError">a maximum of {{ maximumMonthlyInterval }} {{ maximumMonthlyInterval | pluralize('month') }} is allowed</span>
    </div>
  </div>
</template>

<script>

export default {
  name: "PointsExpiration",
  props: {
    isEditable: Boolean,
    expirationOptions: Object
  },
  data() {
    return {
      maximumMonthlyInterval: 1000
    }
  },
  computed: {
    dslvMinError() {
      return this.expirationOptions.expirationType === "visit based"
          && this.expirationOptions.visitBasedType === "days_since_last_visit"
          && this.expirationOptions.daysSinceLastVisit < 30;
    },
    dslvMaxError() {
      return this.expirationOptions.expirationType === "visit based"
          && this.expirationOptions.visitBasedType === "days_since_last_visit"
          && this.expirationOptions.daysSinceLastVisit > 2147483647;
    },
    dslvDecError() {
      return this.expirationOptions.expirationType === "visit based"
        && this.expirationOptions.visitBasedType === "days_since_last_visit"
        && !Number.isInteger(parseFloat(this.expirationOptions.daysSinceLastVisit));
    },
    dspiMinError() {
      return this.expirationOptions.expirationType === "visit based"
          && this.expirationOptions.visitBasedType === "days_since_points_issued"
          && this.expirationOptions.daysSincePointsIssued < 30;
    },
    dspiMaxError() {
      return this.expirationOptions.expirationType === "visit based"
          && this.expirationOptions.visitBasedType === "days_since_points_issued"
          && this.expirationOptions.daysSincePointsIssued > 2147483647;
    },
    dspiDecError() {
      return this.expirationOptions.expirationType === "visit based"
        && this.expirationOptions.visitBasedType === "days_since_points_issued"
        && !Number.isInteger(parseFloat(this.expirationOptions.daysSincePointsIssued));
    },
    monthlyMinError() {
      return this.expirationOptions.expirationType === "time based"
          && this.expirationOptions.monthlyInterval < 1;
    },
    monthlyMaxError() {
      return this.expirationOptions.expirationType === "time based"
          && this.expirationOptions.monthlyInterval > this.maximumMonthlyInterval;
    },
    monthlyDecError() {
      return this.expirationOptions.expirationType === "time based"
          && !Number.isInteger(parseFloat(this.expirationOptions.monthlyInterval));
    },
    hasDslvError() {
      return this.dslvMinError
        || this.dslvMaxError
        || this.dslvDecError
    },
    hasDspiError() {
      return this.dspiMinError
        || this.dspiMaxError
        || this.dspiDecError
    },
    hasMonthlyError() {
      return this.monthlyMinError
        || this.monthlyMaxError
        || this.monthlyDecError
    },
    hasComputedError() {
      return this.hasDslvError
        || this.hasDspiError
        || this.hasMonthlyError
    },
  },
  watch: {
    hasComputedError(hasError) {
      this.$emit('errorChanged', hasError)
    }
  }
}
</script>
